import { useEffect, useState, useCallback } from 'react'
import type { FormEvent } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { ArrowLeft, ChevronLeft, ChevronRight, ExternalLink, Gift, Heart, Link2, Loader2, Ruler, Tag } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import type { Profile } from '@/hooks/useAuth'
import { formatPrice, urlHostname, type WishlistItem } from '@/lib/wishlist'
import { useImageBackground } from '@/hooks/useImageBackground'

type LoadState = 'loading' | 'ready' | 'not-found'

export default function WishlistItemPage() {
  const { username = '', itemId = '' } = useParams<{ username: string; itemId: string }>()
  const [item, setItem] = useState<WishlistItem | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [state, setState] = useState<LoadState>('loading')
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [justReserved, setJustReserved] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [galleryImages, setGalleryImages] = useState<string[]>([])
  const [activeImage, setActiveImage] = useState<string | null>(null)
  const [siblingIds, setSiblingIds] = useState<string[]>([])
  const [isGuide, setIsGuide] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false
    async function load() {
      setState('loading')
      setItem(null)
      setGalleryImages([])
      setActiveImage(null)
      setSiblingIds([])
      setJustReserved(false)
      const { data: itemData, error: itemError } = await supabase
        .from('wishlist_items')
        .select('*')
        .eq('id', itemId)
        .maybeSingle()

      if (cancelled) return
      if (itemError || !itemData) {
        setState('not-found')
        return
      }
      const found = itemData as unknown as WishlistItem
      setItem(found)
      setActiveImage(found.image_url)

      // Guide boards ("shopping advice") have no reservation flow
      if ((found as unknown as { wishlist_id: string | null }).wishlist_id) {
        const { data: boardData } = await supabase
          .from('wishlists')
          .select('kind')
          .eq('id', (found as unknown as { wishlist_id: string | null }).wishlist_id!)
          .maybeSingle()
        if (!cancelled && boardData) {
          setIsGuide((boardData as unknown as { kind: string }).kind === 'guide')
        }
      }

      // Load gallery images
      const { data: galleryData } = await supabase
        .from('wishlist_item_images')
        .select('url')
        .eq('item_id', itemId)
        .order('position', { ascending: true })
      if (!cancelled && galleryData) {
        const urls = galleryData.map((g: { url: string }) => g.url)
        setGalleryImages(urls)
        if (!found.image_url && urls.length > 0) {
          setActiveImage(urls[0])
        }
      }

      const uname = username.toLowerCase()
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uname)
      let profileData: Profile | null = null
      if (isUuid) {
        const { data: byId } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', uname)
          .maybeSingle()
        profileData = (byId as unknown as Profile | null) ?? null
      } else {
        const { data: byUsername } = await supabase
          .from('profiles')
          .select('*')
          .ilike('username', uname)
          .maybeSingle()
        profileData = (byUsername as unknown as Profile | null) ?? null
      }

      if (cancelled) return
      setProfile((profileData as unknown as Profile | null) ?? null)

      // Load sibling item IDs for keyboard navigation
      if (profileData) {
        const profileId = (profileData as unknown as { id: string }).id
        const { data: allItems } = await supabase
          .from('wishlist_items')
          .select('id')
          .eq('user_id', profileId)
          .eq('is_hidden', false)
          .order('position', { ascending: true })
        if (!cancelled && allItems) {
          setSiblingIds((allItems as unknown as { id: string }[]).map((i) => i.id))
        }
      }

      setState('ready')
    }
    load()
    return () => {
      cancelled = true
    }
  }, [itemId, username])

  const currentIndex = siblingIds.indexOf(itemId)
  const prevId = currentIndex > 0 ? siblingIds[currentIndex - 1] : null
  const nextId = currentIndex >= 0 && currentIndex < siblingIds.length - 1 ? siblingIds[currentIndex + 1] : null

  const goToSibling = useCallback((dir: 'prev' | 'next') => {
    const target = dir === 'prev' ? prevId : nextId
    if (target) navigate(`/w/${username}/item/${target}`)
  }, [prevId, nextId, username, navigate])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key === 'ArrowLeft' && prevId) { e.preventDefault(); goToSibling('prev') }
      if (e.key === 'ArrowRight' && nextId) { e.preventDefault(); goToSibling('next') }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [prevId, nextId, goToSibling])

  const reserve = async (e: FormEvent) => {
    e.preventDefault()
    if (!item || !name.trim()) return
    setSaving(true)
    setActionError(null)
    const { error } = await supabase
      .from('wishlist_items')
      .update({ is_reserved: true, reserved_by: name.trim() })
      .eq('id', item.id)
    setSaving(false)
    if (error) {
      setActionError(error.message)
      return
    }
    setItem({ ...item, is_reserved: true, reserved_by: name.trim() })
    setJustReserved(true)
    setName('')
  }

  const unreserve = async () => {
    if (!item) return
    setSaving(true)
    setActionError(null)
    const { error } = await supabase
      .from('wishlist_items')
      .update({ is_reserved: false, reserved_by: null })
      .eq('id', item.id)
    setSaving(false)
    if (error) {
      setActionError(error.message)
      return
    }
    setItem({ ...item, is_reserved: false, reserved_by: null })
    setJustReserved(false)
  }

  const price = item ? formatPrice(item.price) : null
  const shopHost = item ? urlHostname(item.url) : null
  const autoBg = useImageBackground(item?.image_url)
  const imageBg = item?.bg_color || autoBg

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 glass-strong border-b border-border/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold">
            <span className="w-8 h-8 rounded-2xl bg-neutral-900 text-white flex items-center justify-center">
              <Gift className="w-4 h-4" />
            </span>
            Make me a Gift
          </Link>
          <Link to="/register" className="text-sm font-medium text-white bg-neutral-900 rounded-full px-5 py-2.5 hover:bg-neutral-800 transition-colors">
            Crea anche tu la tua wishlist
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {state === 'loading' && (
          <div className="space-y-6">
            <div className="h-4 w-40 bg-muted rounded-full animate-pulse" />
            <div className="aspect-[4/3] bg-muted rounded-2xl animate-pulse" />
            <div className="h-8 w-2/3 bg-muted rounded-full animate-pulse" />
            <div className="h-4 w-full bg-muted rounded-full animate-pulse" />
          </div>
        )}

        {state === 'not-found' && (
          <div className="py-24 text-center">
            <div className="mx-auto w-20 h-20 rounded-full bg-neutral-100 flex items-center justify-center">
              <Heart className="w-10 h-10 text-neutral-900" />
            </div>
            <h1 className="mt-6 text-3xl font-extrabold tracking-tight">Desiderio non trovato</h1>
            <p className="mt-3 text-muted-foreground">Forse è già stato esaudito</p>
            <Link
              to={`/w/${username}`}
              className="inline-block mt-8 text-sm font-medium text-white bg-neutral-900 rounded-full px-5 py-2.5 hover:bg-neutral-800 transition-colors"
            >
              Torna alla wishlist
            </Link>
          </div>
        )}

        {state === 'ready' && item && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link
              to={`/w/${username}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-neutral-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna alla wishlist{profile?.display_name ? ` di ${profile.display_name}` : ''}
            </Link>

            <div className="mt-6 rounded-2xl overflow-hidden glass hover-glow">
              {activeImage ? (
                <img src={activeImage} alt={item.title} className="w-full aspect-[4/3] object-contain" />
              ) : (
                <div className="w-full aspect-[4/3] bg-neutral-100 flex items-center justify-center">
                  <Gift className="w-24 h-24 text-neutral-300" />
                </div>
              )}
            </div>

            {galleryImages.length > 0 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
                {item.image_url && (
                  <button
                    onClick={() => setActiveImage(item.image_url)}
                    className={`shrink-0 overflow-hidden rounded-lg border-2 transition ${activeImage === item.image_url ? 'border-neutral-900' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  >
                    <img src={item.image_url} alt="Principale" className="h-16 w-16 object-cover" />
                  </button>
                )}
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(img)}
                    className={`shrink-0 overflow-hidden rounded-lg border-2 transition ${activeImage === img ? 'border-neutral-900' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  >
                    <img src={img} alt={`Foto ${idx + 1}`} className="h-16 w-16 object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="glass border border-border/30 text-neutral-900 text-xs font-bold px-3 py-1.5 rounded-full">
                {item.category}
              </span>
              {!isGuide && item.is_reserved && (
                <span className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full">
                  Qualcuno ha già scelto questo regalo
                </span>
              )}
              {isGuide && (
                <span className="bg-pink-100 text-pink-700 text-xs font-bold px-3 py-1.5 rounded-full">
                  Consigli
                </span>
              )}
            </div>

            {(prevId || nextId) && (
              <div className="mt-4 flex items-center justify-between gap-3">
                <button
                  onClick={() => goToSibling('prev')}
                  disabled={!prevId}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-neutral-900 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Precedente
                </button>
                <span className="text-xs text-muted-foreground/60">
                  {currentIndex + 1} / {siblingIds.length}
                </span>
                <button
                  onClick={() => goToSibling('next')}
                  disabled={!nextId}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-neutral-900 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                >
                  Successivo
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight">{item.title}</h1>
            {item.description && (
              <p className="mt-4 whitespace-pre-line text-muted-foreground leading-relaxed">{item.description}</p>
            )}

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <div className="glass rounded-2xl p-5 hover-glow">
                <div className="w-10 h-10 rounded-2xl bg-neutral-100 text-neutral-900 flex items-center justify-center">
                  <Ruler className="w-5 h-5" />
                </div>
                <p className="mt-3 text-xs font-semibold text-muted-foreground/70 uppercase tracking-wide">Misura desiderata</p>
                <p className="mt-1 font-bold">{item.size ?? '—'}</p>
              </div>
              <div className="glass rounded-2xl p-5 hover-glow">
                <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center">
                  <Tag className="w-5 h-5" />
                </div>
                <p className="mt-3 text-xs font-semibold text-muted-foreground/70 uppercase tracking-wide">Prezzo indicativo</p>
                <p className="mt-1 font-bold gradient-text-price">{price ?? '—'}</p>
              </div>
              <div className="glass rounded-2xl p-5 hover-glow">
                <div className="w-10 h-10 rounded-2xl bg-neutral-100 text-neutral-900 flex items-center justify-center">
                  <Link2 className="w-5 h-5" />
                </div>
                <p className="mt-3 text-xs font-semibold text-muted-foreground/70 uppercase tracking-wide">Dove acquistarlo</p>
                <p className="mt-1 font-bold">{shopHost ?? '—'}</p>
              </div>
            </div>

            {item.url && (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 bg-neutral-900 text-neutral-900 -foreground font-bold px-8 py-4 rounded-full hover:bg-neutral-800 hover:shadow-lg hover:shadow-primary/25 transition-all"
              >
                Vai al negozio
                <ExternalLink className="w-5 h-5" />
              </a>
            )}

            {!isGuide && (
            <motion.div
              className="mt-8 glass rounded-2xl p-6 sm:p-8 hover-glow"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
            >
              {actionError && (
                <div className="mb-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3">
                  {actionError}
                </div>
              )}

              {!item.is_reserved ? (
                <>
                  <h2 className="text-2xl font-extrabold tracking-tight">Vuoi fare questo regalo?</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Segnalo come scelto così gli altri non lo comprano due volte. Tranquillo: sarà una sorpresa!
                  </p>
                  <form onSubmit={reserve} className="mt-5 flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Il tuo nome, così gli altri sanno che ci pensi tu"
                      className="flex-1 rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-neutral-900 /40 focus:border-neutral-900 placeholder:text-muted-foreground/60"
                    />
                    <button
                      type="submit"
                      disabled={saving}
                      className="bg-primary text-neutral-900 -foreground font-bold px-8 py-3 rounded-full hover:bg-neutral-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                      {saving ? 'Un attimo…' : 'Lo regalo io!'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center">
                  <AnimatePresence>
                    {justReserved && (
                      <motion.div
                        className="mx-auto w-16 h-16 rounded-full bg-neutral-100 text-neutral-900 flex items-center justify-center"
                        initial={{ scale: 0 }}
                        animate={{ scale: [0, 1.3, 1] }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                      >
                        <Heart className="w-8 h-8 fill-current" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <h2 className="mt-4 text-2xl font-extrabold tracking-tight">
                    {justReserved ? 'Grazie, sarà una bellissima sorpresa!' : 'Questo regalo è già stato scelto'}
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {item.reserved_by ? (
                      <>
                        <strong>{item.reserved_by}</strong> ha segnato questo desiderio.
                      </>
                    ) : (
                      'Qualcuno ha segnato questo desiderio.'
                    )}
                  </p>
                  <button
                    onClick={unreserve}
                    disabled={saving}
                    className="mt-5 text-sm font-semibold text-muted-foreground hover:text-red-600 underline underline-offset-4 transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                  >
                    {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Non lo regali più? Annulla
                  </button>
                </div>
              )}
            </motion.div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  )
}
